import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, Users, Compass, CheckCircle2 } from 'lucide-react';
import NewsletterForm from '../components/NewsletterForm';
import SEO from '../components/SEO';
import { DISCOVERY_CALL_URL } from '../constants/site';

// Next scheduled free masterclass — kept manually in sync.
// Same source-of-truth cadence as the /academy live sessions.
const MASTERCLASS_STATUS_LABEL = 'Sign up for the upcoming date';
const MASTERCLASS_DURATION = '45 minutes';

const BULLETS = [
  {
    icon: Compass,
    title: 'What operators actually screen for',
    body: 'Why "years of service" is not the currency — and what an operator is really deciding when they read your CV.',
  },
  {
    icon: Users,
    title: 'The 80% behind the plate',
    body: 'A working walkthrough of catering ordering, provisioning and sequence — the part of the job no restaurant, hotel or airline will have taught you.',
  },
  {
    icon: Video,
    title: 'Live Q&A with Nicolas',
    body: 'A working corporate flight attendant on Bombardier Global and Challenger series. Ask the operational questions you cannot Google.',
  },
];

const MasterclassPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: 'Gate 33 Free Masterclass — Break Into Private Aviation',
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      description:
        'A free 45-minute live masterclass with Nicolas — a working corporate flight attendant on Bombardier Global and Challenger aircraft. What operators actually screen for, and the operations layer no service background covers. Sign up to be notified when the next date opens.',
      organizer: { '@type': 'Organization', name: 'Gate 33', url: 'https://gate33.co' },
      location: { '@type': 'VirtualLocation', url: 'https://gate33.co/masterclass' },
    }),
    [],
  );

  return (
    <div data-testid="masterclass-page" className="min-h-screen">
      <SEO
        title="Free Masterclass — Break Into Private Aviation | Gate 33"
        description="A free 45-minute live masterclass with Nicolas. What operators actually screen for, and the operations layer behind every private jet cabin — for commercial crew, hospitality professionals and career changers."
        path="/masterclass"
        jsonLd={jsonLd}
      />

      {/* ===== HERO ===== */}
      <section data-testid="masterclass-hero" className="relative min-h-[70vh] flex items-center bg-[#FDFBF7]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 w-full">
          <span className="overline inline-block mb-6">Free · Live · {MASTERCLASS_DURATION}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F0F0F] mb-6 max-w-3xl">
            The operations layer no service background will teach you.
          </h1>
          <p className="text-lg text-[#1A1A1A] leading-relaxed max-w-2xl mb-6">
            A free 45-minute live masterclass with Nicolas — a working corporate flight attendant on Bombardier Global and Challenger series aircraft. For commercial crew, hospitality professionals and career changers considering the transition into business aviation.
          </p>
          <div
            data-testid="masterclass-date-badge"
            className="inline-flex items-center gap-3 border border-[#C5A880] bg-white px-4 py-2.5"
          >
            <span className="w-2 h-2 rounded-full bg-[#C5A880] animate-pulse" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#0F0F0F] font-semibold">
              {MASTERCLASS_STATUS_LABEL}
            </span>
          </div>
        </div>
      </section>

      {/* ===== 3 BULLETS ===== */}
      <section data-testid="masterclass-bullets" className="section-padding bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <span className="overline inline-block mb-4">What we cover</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F] mb-12 max-w-2xl">
            Three concrete things you leave with.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BULLETS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`masterclass-bullet-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="border border-[#E5E0D8] p-8 bg-[#FDFBF7]"
                >
                  <Icon size={22} className="text-[#C5A880] mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg mb-3 text-[#0F0F0F] leading-snug">{b.title}</h3>
                  <p className="text-sm text-[#5A5A5A] leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== REGISTER ===== */}
      <section data-testid="masterclass-register" className="section-dark py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">
              Register — Free
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#FDFBF7] mb-4">
              Save your seat.
            </h2>
            <p className="text-[#9A9A9A] leading-relaxed max-w-xl mx-auto">
              Leave your email — you&apos;ll be notified as soon as the next date is confirmed and receive the joining link a few days before the session. Live only, no replay.
            </p>
          </div>

          <div className="bg-[#1A1A1A] border border-[#C5A880]/30 p-8">
            <NewsletterForm
              source="masterclass"
              variant="dark"
              compact
              testId="masterclass-register-form"
            />
          </div>

          <p className="mt-6 text-xs text-[#9A9A9A] text-center">
            We only use your details to send you the joining link and, if you opt in, occasional cohort news.
            Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* ===== CLOSER ===== */}
      <section data-testid="masterclass-closer" className="section-padding bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="border border-[#E5E0D8] p-10 md:p-12 bg-[#FDFBF7]">
            <div className="flex items-start gap-4 mb-4">
              <CheckCircle2 size={20} className="text-[#C5A880] flex-shrink-0 mt-1" />
              <h3 className="font-heading text-xl text-[#0F0F0F] leading-snug">
                Prefer to talk it through directly?
              </h3>
            </div>
            <p className="text-[#5A5A5A] leading-relaxed mb-8 pl-9">
              Book a free 45-minute Discovery Call and get a personal read on your profile before the next masterclass.
            </p>
            <div className="pl-9 flex flex-wrap gap-3">
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="masterclass-discovery-cta"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Book a Discovery Call <ArrowRight size={14} />
              </a>
              <Link
                to="/academy"
                data-testid="masterclass-academy-link"
                className="text-xs uppercase tracking-[0.18em] text-[#5A5A5A] hover:text-[#C5A880] transition-colors self-center"
              >
                See the 3-Day Training →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MasterclassPage;
